'use client';

import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Typography,
} from '@mui/material';
import Image from 'next/image';
import Link from 'next/link';

import { Project } from '../../../services/types/project';
import { getImageAlt, getImageUrl } from '../../../services/utils/image';

export interface ProjectCardProps {
  project: Project;
  variant?: 'default' | 'featured' | 'compact';
}

const ProjectCard = ({ project, variant = 'default' }: ProjectCardProps) => {
  const imageUrl = getImageUrl(project.image || null);
  const imageAlt = getImageAlt(project.image || null);

  const cardStyles = {
    default: {
      height: '100%',
      width: 600,
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
      },
    },
    featured: {
      height: '100%',
      width: 600,
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 35px rgba(0, 0, 0, 0.2)',
      },
    },
    compact: {
      height: '100%',
      width: 600,
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
      },
    },
  };

  const mediaStyles = {
    default: {
      height: 240,
      position: 'relative',
      backgroundColor: '#f5f5f5',
    },
    featured: {
      height: 280,
      position: 'relative',
      backgroundColor: '#f5f5f5',
    },
    compact: {
      height: 180,
      position: 'relative',
      backgroundColor: '#f5f5f5',
    },
  };

  const contentStyles = {
    default: {
      flexGrow: 1,
      display: 'flex',
      padding: '20px',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    featured: {
      flexGrow: 1,
      display: 'flex',
      padding: '24px',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    compact: {
      flexGrow: 1,
      display: 'flex',
      padding: '16px',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
  };

  return (
    <Link href={`/projects/${project.slug}`} style={{ textDecoration: 'none' }}>
      <Card sx={cardStyles[variant]}>
        <CardMedia sx={mediaStyles[variant]}>
          {project.image ? (
            <Image
              src={imageUrl}
              alt={imageAlt}
              fill
              style={{ objectFit: 'cover' }}
              sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
            />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f5f5f5',
                color: '#999',
              }}
            >
              <Typography variant='body2'>Không có hình ảnh</Typography>
            </Box>
          )}
        </CardMedia>
        <CardContent sx={contentStyles[variant]}>
          <Box>
            <Typography
              variant={variant === 'featured' ? 'h6' : 'subtitle1'}
              component='h3'
              sx={{
                fontWeight: 600,
                color: '#1A1A1A',
                marginBottom: 1,
                lineHeight: 1.3,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {project.title}
            </Typography>

            {project.location && (
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{
                  marginBottom: 2,
                  flexGrow: 1,
                  display: '-webkit-box',
                  WebkitLineClamp: variant === 'compact' ? 2 : 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  lineHeight: 1.5,
                }}
              >
                {project.location}
              </Typography>
            )}
          </Box>

          <Box>
            <Button
              size='medium'
              color='primary'
              sx={{
                backgroundColor: 'transparent',
                marginTop: 'auto',
                borderRadius: '4px',
                textTransform: 'none',
                width: '150px',
                height: '40px',
                color: '#f5f5f5',
                fontWeight: 800,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#EB722E',
                  color: '#fff',
                },
                '&:active': {
                  backgroundColor: '#D65A1A',
                  color: '#fff',
                },
              }}
            >
              {'Xem dự án'} {'-->'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Link>
  );
};

export default ProjectCard;
